/**
 * Support ticket modal component.
 */

const MAX_ERRORS = 20;

let errorBuffer = [];
let attachmentUuids = [];
let uploading = false;

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^|;)\\s*${name}=([^;]+)`));
  return match ? decodeURIComponent(match.pop()) : '';
}

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  if (meta && meta.content) {
    return meta.content;
  }
  const input = document.querySelector('input[name="csrfmiddlewaretoken"]');
  if (input && input.value) {
    return input.value;
  }
  return getCookie('csrftoken');
}

function buildClientInfo() {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    devicePixelRatio: window.devicePixelRatio,
    currentUrl: window.location.href,
    timestamp: new Date().toISOString(),
  };
}

function pushError(entry) {
  errorBuffer.push({ ...entry, time: new Date().toISOString() });
  if (errorBuffer.length > MAX_ERRORS) {
    errorBuffer = errorBuffer.slice(-MAX_ERRORS);
  }
}

function setupErrorBuffer() {
  window.addEventListener('error', (event) => {
    pushError({
      message: event.message,
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error && event.error.stack ? event.error.stack : '',
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    let message = '';
    let stack = '';
    if (reason instanceof Error) {
      message = reason.message;
      stack = reason.stack || '';
    } else if (typeof reason === 'string') {
      message = reason;
    } else {
      try {
        message = JSON.stringify(reason);
      } catch (_e) {
        message = String(reason);
      }
    }
    pushError({ message, source: '', lineno: 0, colno: 0, stack });
  });
}

function showStepFaq(modal) {
  const stepFaq = modal.querySelector('[data-cy="support-step-faq"]');
  const stepForm = modal.querySelector('[data-cy="support-step-form"]');
  const success = modal.querySelector('[data-cy="support-success"]');
  if (stepFaq) stepFaq.classList.remove('d-none');
  if (stepForm) stepForm.classList.add('d-none');
  if (success) success.classList.add('d-none');
}

function showStepForm(modal) {
  const stepFaq = modal.querySelector('[data-cy="support-step-faq"]');
  const stepForm = modal.querySelector('[data-cy="support-step-form"]');
  const success = modal.querySelector('[data-cy="support-success"]');
  if (stepFaq) stepFaq.classList.add('d-none');
  if (stepForm) stepForm.classList.remove('d-none');
  if (success) success.classList.add('d-none');
}

function showSuccess(modal, ticketUuid) {
  const stepFaq = modal.querySelector('[data-cy="support-step-faq"]');
  const stepForm = modal.querySelector('[data-cy="support-step-form"]');
  const success = modal.querySelector('[data-cy="support-success"]');
  const ref = modal.querySelector('[data-cy="support-reference"]');
  if (stepFaq) stepFaq.classList.add('d-none');
  if (stepForm) stepForm.classList.add('d-none');
  if (success) success.classList.remove('d-none');
  if (ref) ref.textContent = ticketUuid;
}

async function handleFiles(files, statusEl, hiddenInput, submitBtn) {
  if (!files || files.length === 0) return;
  uploading = true;
  if (submitBtn) submitBtn.disabled = true;
  if (statusEl) statusEl.textContent = 'Uploading…';
  const newUuids = [];
  let failed = false;
  for (const file of files) {
    if (statusEl) statusEl.textContent = `Uploading ${file.name}…`;
    try {
      const createResp = await fetch('/files/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify({
          context: 'SUPPORT_TICKET',
          filename: file.name,
          size: file.size,
          content_type: file.type || 'application/octet-stream',
        }),
      });
      const createData = await createResp.json().catch(() => null);
      if (!createResp.ok || !createData) {
        throw new Error((createData && createData.error) || `Upload failed (HTTP ${createResp.status})`);
      }
      const fileUuid = createData.file_uuid;
      const formData = new FormData();
      formData.append('file', file);
      const uploadResp = await fetch(`/files/${fileUuid}/upload/`, {
        method: 'POST',
        headers: { 'X-CSRFToken': getCsrfToken() },
        body: formData,
      });
      if (!uploadResp.ok) {
        const errData = await uploadResp.json().catch(() => null);
        throw new Error((errData && errData.error) || `Upload failed (HTTP ${uploadResp.status})`);
      }
      newUuids.push(fileUuid);
      if (statusEl) statusEl.textContent = `Uploaded ${file.name}`;
    } catch (err) {
      failed = true;
      if (statusEl) statusEl.textContent = err.message || 'Upload failed';
      break;
    }
  }
  if (!failed) {
    attachmentUuids = attachmentUuids.concat(newUuids);
    if (hiddenInput) hiddenInput.value = JSON.stringify(attachmentUuids);
    if (statusEl) statusEl.textContent = newUuids.length ? `Uploaded ${newUuids.length} file(s)` : '';
  }
  uploading = false;
  if (submitBtn) submitBtn.disabled = failed;
}

export function init(rootEl = document) {
  setupErrorBuffer();
  const modal = rootEl.getElementById ? rootEl.getElementById('supportModal') : document.getElementById('supportModal');
  if (!modal) return;
  const stepFaqBtn = modal.querySelector('[data-cy="support-create-ticket"]');
  const form = modal.querySelector('#support_ticket_form');
  const fileInput = modal.querySelector('[data-cy="support-attachments"]');
  const statusEl = modal.querySelector('[data-cy="support-upload-status"]');
  const submitBtn = modal.querySelector('[data-cy="support-submit"]');
  const errorEl = modal.querySelector('[data-cy="support-form-error"]');

  function resetModal() {
    // Always return to the FAQ step on (re)open, even after a submission, so
    // a fresh support case is shown instead of the previous success panel.
    showStepFaq(modal);
    const ref = modal.querySelector('[data-cy="support-reference"]');
    if (ref) ref.textContent = '';
    attachmentUuids = [];
    uploading = false;
    if (form) {
      const hiddenUuids = form.querySelector('input[name="attachment_uuids"]');
      if (hiddenUuids) hiddenUuids.value = '[]';
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.add('d-none');
      }
      if (statusEl) statusEl.textContent = '';
      if (submitBtn) submitBtn.disabled = false;
      if (fileInput) fileInput.value = '';
    }
  }

  modal.addEventListener('show.bs.modal', () => {
    resetModal();
    const clientInfoInput = form ? form.querySelector('input[name="client_info"]') : null;
    const recentErrorsInput = form ? form.querySelector('input[name="recent_errors"]') : null;
    if (clientInfoInput) clientInfoInput.value = JSON.stringify(buildClientInfo());
    if (recentErrorsInput) recentErrorsInput.value = JSON.stringify(errorBuffer);
  });

  if (stepFaqBtn) {
    stepFaqBtn.addEventListener('click', () => {
      showStepForm(modal);
    });
  }

  if (fileInput && form) {
    const hiddenUuids = form.querySelector('input[name="attachment_uuids"]');
    fileInput.addEventListener('change', () => {
      handleFiles(fileInput.files, statusEl, hiddenUuids, submitBtn);
    });
  }

  if (form) {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (uploading) return;
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.add('d-none');
      }
      if (submitBtn) submitBtn.disabled = true;
      const clientInfoInput = form.querySelector('input[name="client_info"]');
      const recentErrorsInput = form.querySelector('input[name="recent_errors"]');
      const hiddenUuids = form.querySelector('input[name="attachment_uuids"]');
      if (clientInfoInput) clientInfoInput.value = JSON.stringify(buildClientInfo());
      if (recentErrorsInput) recentErrorsInput.value = JSON.stringify(errorBuffer);
      const email = form.querySelector('[data-cy="support-email"]')?.value || '';
      const issueType = form.querySelector('[data-cy="support-issue-type"]')?.value || '';
      const subject = form.querySelector('[data-cy="support-subject"]')?.value || '';
      const description = form.querySelector('[data-cy="support-description"]')?.value || '';
      let clientInfo = null;
      let recentErrors = null;
      let attachmentList = [];
      try {
        clientInfo = clientInfoInput && clientInfoInput.value ? JSON.parse(clientInfoInput.value) : null;
      } catch (_e) {
        clientInfo = null;
      }
      try {
        recentErrors = recentErrorsInput && recentErrorsInput.value ? JSON.parse(recentErrorsInput.value) : null;
      } catch (_e) {
        recentErrors = null;
      }
      try {
        attachmentList = hiddenUuids && hiddenUuids.value ? JSON.parse(hiddenUuids.value) : [];
      } catch (_e) {
        attachmentList = [];
      }
      const url = form.getAttribute('data-url') || '/support/tickets/';
      try {
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({
            email,
            issue_type: issueType,
            subject,
            description,
            client_info: clientInfo,
            recent_errors: recentErrors,
            attachment_uuids: attachmentList,
          }),
        });
        const data = await resp.json().catch(() => null);
        if (!resp.ok || !data || !data.success) {
          const msg = (data && (data.error || JSON.stringify(data.error))) || `Request failed (HTTP ${resp.status})`;
          throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
        }
        showSuccess(modal, data.ticket_uuid);
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = err.message || 'Submission failed';
          errorEl.classList.remove('d-none');
        }
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  }

  // Auto-open the modal when arriving with ?open_support=1 (e.g. from the
  // FAQ page's Need Support button) so the user can raise a ticket in one click.
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('open_support') === '1') {
    const trigger = document.querySelector(
      '[data-cy="settings-support-trigger"], [data-cy="support-trigger"]',
    );
    if (trigger) {
      trigger.click();
    }
  }
}
